/**
 * Dialog de configuração para node de adição de tags
 * Suporta tags de contato e negócio (CRM)
 * Permite criar tags rapidamente sem sair do dialog
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo dialog unificado
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FlowNode, AddTagConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Plus, X, HelpCircle } from 'lucide-react';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { Separator } from '@/components/ui/separator';
import { NodeHelpDialog } from './NodeHelpDialog';
import { VariablePickerCombobox } from '../VariablePickerCombobox';

/**
 * Schema de validação para adição de tag
 */
const addTagSchema = z.object({
  scope: z.enum(['contact', 'deal']),
  tag_id: z.string().uuid('ID da tag inválido'),
  tag_name: z.string().trim().min(1, 'Nome da tag é obrigatório'),
  tag_color: z.string().optional(),
  deal_selection: z.enum(['most_recent', 'from_variable']).optional(),
  deal_status_filter: z.enum(['open', 'won', 'lost', 'all']).optional(),
  deal_variable_id: z.string().optional(),
  deal_variable_name: z.string().optional(),
}).refine((data) => {
  // Se modo from_variable, precisa ter variável selecionada
  if (data.scope === 'deal' && data.deal_selection === 'from_variable') {
    return !!data.deal_variable_id || !!data.deal_variable_name;
  }
  return true;
}, {
  message: 'Selecione uma variável com o ID do negócio',
  path: ['deal_variable_id'],
});

interface ConfigureAddTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: any) => void;
}

/**
 * Cores preset para criação rápida de tags
 */
const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#0ea5e9', '#3b82f6',
];

/**
 * Dialog de configuração para adicionar tags
 */
export function ConfigureAddTagDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureAddTagDialogProps) {
  const { currentTenant } = useAuth();
  const [loadingTags, setLoadingTags] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados do formulário
  const [scope, setScope] = useState<'contact' | 'deal'>('contact');
  const [tagId, setTagId] = useState('');
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);
  
  // Estados para identificação de deal
  const [dealSelection, setDealSelection] = useState<'most_recent' | 'from_variable'>('most_recent');
  const [dealStatusFilter, setDealStatusFilter] = useState<'open' | 'won' | 'lost' | 'all'>('open');
  const [dealVariableId, setDealVariableId] = useState('');
  const [dealVariableName, setDealVariableName] = useState('');
  
  // Estados para criação rápida de tag
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [creatingTag, setCreatingTag] = useState(false);
  
  // Flag para evitar limpeza de tagId durante inicialização
  const isInitializing = useRef(true);

  /**
   * Carrega tags baseado no escopo selecionado
   */
  const loadTags = async () => {
    if (!currentTenant?.id) return;
    
    setLoadingTags(true);
    try {
      if (scope === 'contact') {
        // Carrega tags de contato
        const { data, error } = await supabase
          .from('contact_tags')
          .select('id, name, color')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (error) {
          toast.error('Erro ao carregar tags de contato');
          return;
        }
        setTags(data || []);
      } else {
        // Carrega tags de negócio (CRM)
        const { data, error } = await supabase
          .from('crm_deal_tags')
          .select('id, name, color')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (error) {
          toast.error('Erro ao carregar tags de negócio');
          return;
        }
        setTags(data || []);
      }
    } finally {
      setLoadingTags(false);
    }
  };

  // Carrega tags quando dialog abre ou escopo muda
  useEffect(() => {
    if (open && currentTenant?.id) {
      loadTags();
    }
  }, [open, currentTenant?.id, scope]);

  // Inicializa valores do node
  useEffect(() => {
    if (node && node.data.config) {
      const config = node.data.config as AddTagConfig;
      // Marca que está inicializando para não limpar tagId no useEffect do scope
      isInitializing.current = true;
      setScope(config.scope || 'contact');
      setTagId(config.tag_id || config.tagId || '');
      setDealSelection(config.deal_selection || 'most_recent');
      setDealStatusFilter(config.deal_status_filter || 'open');
      setDealVariableId(config.deal_variable_id || '');
      setDealVariableName(config.deal_variable_name || '');
      // Libera flag após pequeno delay para permitir que useEffect do scope execute primeiro
      setTimeout(() => { isInitializing.current = false; }, 0);
    } else {
      // Reset para valores padrão
      setScope('contact');
      setTagId('');
      setDealSelection('most_recent');
      setDealStatusFilter('open');
      setDealVariableId('');
      setDealVariableName('');
      isInitializing.current = false;
    }
  }, [node]);

  // Limpa seleção de tag ao mudar escopo (apenas se não for inicialização)
  useEffect(() => {
    // Durante inicialização, não limpar os valores carregados da config
    if (isInitializing.current) return;
    
    setTagId('');
    setShowCreateTag(false);
  }, [scope]);

  /**
   * Cria nova tag rapidamente sem sair do dialog
   */
  const handleCreateTag = async () => {
    if (!newTagName.trim() || !currentTenant?.id) return;
    
    setCreatingTag(true);
    try {
      // Tabela depende do escopo
      const tableName = scope === 'contact' ? 'contact_tags' : 'crm_deal_tags';
      
      const { data, error } = await supabase
        .from(tableName)
        .insert({
          tenant_id: currentTenant.id,
          name: newTagName.trim(),
          color: newTagColor,
        })
        .select('id, name, color')
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('Já existe uma tag com este nome');
          return;
        }
        throw error;
      }

      // Adiciona a nova tag à lista e seleciona automaticamente
      setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setTagId(data.id);
      toast.success('Tag criada com sucesso');
      
      // Limpa e fecha o mini-modal
      setNewTagName('');
      setNewTagColor('#6366f1');
      setShowCreateTag(false);
    } catch (error) {
      toast.error('Erro ao criar tag');
    } finally {
      setCreatingTag(false);
    }
  };

  /**
   * Salva a configuração do node
   */
  const handleSave = () => {
    if (!node) return;

    try {
      const selectedTag = tags.find(t => t.id === tagId);
      if (!selectedTag) {
        toast.error('Selecione uma tag válida');
        return;
      }
      
      const config: AddTagConfig = {
        scope,
        tag_id: tagId,
        tag_name: selectedTag.name,
        tag_color: selectedTag.color,
        deal_selection: scope === 'deal' ? dealSelection : undefined,
        deal_status_filter: scope === 'deal' && dealSelection === 'most_recent' ? dealStatusFilter : undefined,
        deal_variable_id: scope === 'deal' && dealSelection === 'from_variable' ? dealVariableId : undefined,
        deal_variable_name: scope === 'deal' && dealSelection === 'from_variable' ? dealVariableName : undefined,
      };

      addTagSchema.parse(config);

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() });
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  if (!node) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Adicionar Tag</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />
            
            {/* Tipo de Tag */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Tipo de Tag *</Label>
              <RadioGroup 
                value={scope} 
                onValueChange={(v) => setScope(v as 'contact' | 'deal')} 
                className="flex flex-col gap-2"
              >
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="contact" id="scope-contact" />
                  <Label htmlFor="scope-contact" className="flex-1 cursor-pointer">
                    <div className="font-medium">Tag de Contato</div>
                    <div className="text-xs text-muted-foreground">Tags associadas ao contato</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="deal" id="scope-deal" />
                  <Label htmlFor="scope-deal" className="flex-1 cursor-pointer">
                    <div className="font-medium">Tag de Negócio (CRM)</div>
                    <div className="text-xs text-muted-foreground">Tags associadas a negócios</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Separator />

            {/* Seleção de Tag */}
            <div className="space-y-2">
              <Label>Tag *</Label>
              <div className="flex gap-2">
                <Select value={tagId} onValueChange={setTagId} disabled={loadingTags}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={loadingTags ? 'Carregando...' : 'Selecione uma tag'} />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingTags ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />Carregando...
                      </div>
                    ) : tags.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        Nenhuma tag cadastrada.
                      </div>
                    ) : (
                      tags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: tag.color || '#6366f1' }} 
                            />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {/* Botão + para criar nova tag */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowCreateTag(true)}
                  title="Criar nova tag"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                A tag será adicionada automaticamente ao {scope === 'deal' ? 'negócio' : 'contato'} durante a execução do fluxo
              </p>
            </div>

            {/* Mini-modal para criar tag rápida */}
            {showCreateTag && (
              <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Nova Tag</Label>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6" 
                    onClick={() => {
                      setShowCreateTag(false);
                      setNewTagName('');
                      setNewTagColor('#6366f1');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    placeholder="Nome da tag"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    maxLength={50}
                    className="flex-1"
                    autoFocus
                  />
                </div>
                
                {/* Cores preset */}
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                      style={{ 
                        backgroundColor: color,
                        borderColor: newTagColor === color ? 'hsl(var(--primary))' : 'transparent',
                        boxShadow: newTagColor === color ? `0 0 0 2px ${color}` : 'none'
                      }}
                      onClick={() => setNewTagColor(color)}
                    />
                  ))}
                </div>
                
                <Button 
                  onClick={handleCreateTag} 
                  disabled={!newTagName.trim() || creatingTag}
                  className="w-full"
                  size="sm"
                >
                  {creatingTag ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Tag
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Configuração de identificação de deal (quando scope = 'deal') */}
            {scope === 'deal' && (
              <>
                <Separator />
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Identificar Negócio</Label>
                  
                  {/* RadioGroup para modo de seleção */}
                  <RadioGroup
                    value={dealSelection}
                    onValueChange={(v) => setDealSelection(v as 'most_recent' | 'from_variable')}
                    className="space-y-2"
                  >
                    <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                      <RadioGroupItem value="most_recent" id="deal-most-recent" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="deal-most-recent" className="font-medium cursor-pointer">
                          Negócio mais recente do contato
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Busca automaticamente o negócio mais recente associado ao contato
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                      <RadioGroupItem value="from_variable" id="deal-from-variable" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="deal-from-variable" className="font-medium cursor-pointer">
                          ID do negócio em variável
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Usa o ID do negócio armazenado em uma variável do fluxo
                        </p>
                      </div>
                    </div>
                  </RadioGroup>

                  {/* Filtro de status (apenas para most_recent) */}
                  {dealSelection === 'most_recent' && (
                    <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                      <Label>Filtro de Status</Label>
                      <Select value={dealStatusFilter} onValueChange={(v) => setDealStatusFilter(v as any)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Aberto</SelectItem>
                          <SelectItem value="won">Ganho</SelectItem>
                          <SelectItem value="lost">Perdido</SelectItem>
                          <SelectItem value="all">Qualquer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* VariablePickerCombobox (apenas para from_variable) */}
                  {dealSelection === 'from_variable' && (
                    <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                      <Label>Variável com ID do Negócio *</Label>
                      <VariablePickerCombobox
                        tenantId={currentTenant?.id || ''}
                        value={dealVariableId || dealVariableName}
                        onChange={(variable) => {
                          if (variable) {
                            setDealVariableId(variable.id);
                            // CORREÇÃO: Usar display_name para exportação correta de variáveis
                            setDealVariableName(variable.display_name || variable.name);
                          } else {
                            setDealVariableId('');
                            setDealVariableName('');
                          }
                        }}
                        allowCreate={false}
                        allowClear={false}
                        placeholder="Selecione a variável com o ID do negócio"
                      />
                      <p className="text-xs text-muted-foreground">
                        Use "ID do Negócio" (gatilho CRM) ou variável do node "Criar Negócio"
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="add_tag" />
    </>
  );
}
